import { GoalThreshold } from '../../coaching-base/config';
import {
    CommitmentApprovalDto,
    CommitmentKpi,
    GoalStatus,
} from '../../coaching-base/general';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import { FocusKPI } from '../models/commitment.model';

import { GoalCategoryItemView } from './goal-category-item-view.model';
import { goalCategoryItemViewTransformer } from './goal-category-item-view.transformer';
import { GoalCategoryType } from './goal-category-type.model';
import { GoalCategoryView } from './goal-category-view.model';

const pendingStatuses: GoalStatus[] = [
    GoalStatus.PRE_APPROVAL,
    GoalStatus.UPDATING,
    GoalStatus.PENDING_CLOSE,
];

function matchesActive(
    e: GoalCategoryItemView,
    approval: CommitmentApprovalDto,
): boolean {
    return (
        (e.goal.statusId === GoalStatus.ACTIVE &&
            e.goal.isOriginalGoal &&
            !e.isOriginalWithDraftPendingApproval) ||
        (pendingStatuses.includes(e.goal.statusId) &&
            !e.goal.isOriginalGoal &&
            approval != null &&
            approval.approved == null)
    );
}

export function categorizeGoals(
    commitmentKpis: CommitmentKpi[],
    goalsFromOtherCommitments: CommitmentKpi[],
    focusKpis: FocusKPI[],
    goalThresholds: GoalThreshold[],
    approval: CommitmentApprovalDto,
    authUserId: number,
): GoalCategoryView[] {
    const cmtItems = goalCategoryItemViewTransformer(
        commitmentKpis || [],
        goalThresholds,
        approval,
        authUserId,
    );
    const otherItems = goalCategoryItemViewTransformer(
        goalsFromOtherCommitments || [],
        goalThresholds,
        approval,
        authUserId,
        true,
    );
    const kpiItems = focusKpis || [];

    const nextCategories: GoalCategoryView[] = new Array<GoalCategoryView>(5);
    nextCategories[GoalCategoryType.DRAFT] = {
        label: TranslationKeyMap._9476_DRAFT,
        items: cmtItems.filter(
            (e) =>
                (e.goal.statusId == null || // statusId should never be null, but still check
                    e.goal.statusId === GoalStatus.DRAFT ||
                    e.goal.statusId === GoalStatus.ACTIVE ||
                    (e.goal.statusId === GoalStatus.PENDING_CLOSE &&
                        !e.goal.diffItemId)) &&
                !e.goal.isOriginalGoal &&
                !e.disputed,
        ),
    };
    nextCategories[GoalCategoryType.ACTIVE_FOR_THIS_COMMITMENT] = {
        label: TranslationKeyMap._9829_ACTIVE_FOR_THIS_COMMITMENT,
        items: cmtItems.filter(
            (e) =>
                (matchesActive(e, approval) ||
                    (e.goal.shared && e.goal.isOriginalGoal)) &&
                e.goal.metricId != null &&
                e.goal.statusId !== GoalStatus.CLOSED &&
                !e.isOriginalWithDraftPendingApproval,
        ),
    };
    nextCategories[GoalCategoryType.ACTIVE_FOR_OTHER_COMMITMENT] = {
        label: TranslationKeyMap._9841_ACTIVE_FOR_OTHER_COMMITMENT,
        items: otherItems.filter(
            (e) =>
                e.goal.statusId === GoalStatus.ACTIVE &&
                e.goal.isOriginalGoal && // only non-updating goals should be from other commitments, but enforce anyway
                !e.disputed && // items from other commitments should never even be disputed, but enforce anyway
                e.goal.metricId != null,
        ),
    };
    nextCategories[GoalCategoryType.RECENTLY_CLOSED] = {
        label: TranslationKeyMap._9563_RECENTLY_CLOSED,
        items: cmtItems.filter(
            (e) => e.goal.statusId === GoalStatus.CLOSED && !e.disputed,
        ),
    };
    nextCategories[GoalCategoryType.DISPUTED] = {
        label: TranslationKeyMap._8703_DISPUTED,
        items: cmtItems.filter((e) => !e.goal.isOriginalGoal && e.disputed),
    };

    const draftItems = kpiItems
        .filter(
            (e) =>
                e.metricId != null &&
                !cmtItems.some(
                    (ee) =>
                        ee.goal.metricId != null &&
                        ee.goal.metricId === e.metricId,
                ),
        )
        .map((e) => ({
            metricId: e.metricId,
            metricName: e.goal_Kpi,
            target: e.target,
            actual: e.actual,
            percentToTarget: e.percentToTarget,
            statusId: GoalStatus.DRAFT,
            tracking_id: e.tracking_id,
        }));
    nextCategories[GoalCategoryType.DRAFT].items.push(
        ...goalCategoryItemViewTransformer(
            draftItems,
            goalThresholds,
            null,
            null,
        ),
    );

    nextCategories.forEach((e, ee) => {
        e.isOpen = e.items.length !== 0;
    });

    return nextCategories;
}
